import { RecentStayData, ActivityEdge, EarningsSummaryModel, BrandStoreConfig } from './EarningsSummary.types';
import {
  EARNINGS_SUMMARY_CONSTANTS,
  MEMBER_STATUS,
  CORE_LEVEL_CODES,
  constants,
  PROMOTIONS_CONSTANT,
} from '../../modules/utils/constants/constants';
import { RootPromotions } from '../../organisms/NewPromotions/NewPromotions.types';
import { getMemberStatus, normalizeTierCode } from '../../modules/utils/memberSnapshotHelper';
import { getImageDomain, getFallbackImage } from '../../modules/utils/imageUtility';
import { getMemberLevelPropertiesByCode } from '../../modules/utils/helper';

/**
 * Replaces placeholders {0}, {1}, {2}, etc. in template string with provided values
 * Example: replaceText("You are {0} {1} away from {2}", ["3", "nights", "Gold Elite"])
 * Returns: "You are 3 nights away from Gold Elite"
 */
const replaceText = (template: string, replacements: string[]): string => {
  if (!template) return '';
  return template.replace(/\{(\d+)\}/g, (match, index) => replacements[index] || match);
};

/**
 * Calculates number of nights between start and end dates
 */
const calculateNights = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

  return Math.max(0, nights);
};

/**
 * Calculates Elite Night Credits based on brand and number of nights
 * Aligned with official Marriott Bonvoy ENC rules:
 * - Most brands: 1 ENC per night
 * - PR (Protea), XE (City Express), XF (Four Points Flex), SE (Series): 0.5 ENC per night
 * - RS (StudioRes): 0 ENCs
 */
/**
 * Calculates Elite Night Credits based on brand and number of nights.
 * Brand lists can be provided from AEM (halfEncBrands, zeroEncBrands) for runtime
 * configurability. Falls back to hardcoded constants when AEM props are empty/undefined.
 */
export const calculateENCsByBrand = (
  numberOfNights: number,
  brandId: string | undefined,
  halfEncBrands?: string[],
  zeroEncBrands?: string[]
): number => {
  if (!brandId || numberOfNights <= 0) return 0;

  const upperBrandId = brandId.toUpperCase();

  const zeroList = (zeroEncBrands ?? []).filter(b => b.trim() !== '').map(b => b.toUpperCase());
  const halfList = (halfEncBrands ?? []).filter(b => b.trim() !== '').map(b => b.toUpperCase());

  if (zeroList.includes(upperBrandId)) {
    return 0;
  }

  if (halfList.includes(upperBrandId)) {
    return Math.floor(numberOfNights * 0.5);
  }

  return numberOfNights;
};

/**
 * Gets target tier for progression based on current tier and member status
 */
const getTargetTierForBenefits = (
  levelTypeCode: string | undefined,
  memberStatus: 'not-near' | 'near-renewing' | 'near-achieving'
): string => {
  const normalizedLevelTypeCode = normalizeTierCode(levelTypeCode);

  if (memberStatus === MEMBER_STATUS.NEAR_ACHIEVING) {
    switch (normalizedLevelTypeCode) {
      case CORE_LEVEL_CODES[0]:
        return CORE_LEVEL_CODES[1];
      case CORE_LEVEL_CODES[1]:
        return CORE_LEVEL_CODES[2];
      case CORE_LEVEL_CODES[2]:
        return CORE_LEVEL_CODES[3];
      case CORE_LEVEL_CODES[3]:
        return 'PPC';
      case 'PPC':
        return CORE_LEVEL_CODES[5];
      default:
        return normalizedLevelTypeCode || CORE_LEVEL_CODES[0];
    }
  }

  return normalizedLevelTypeCode || CORE_LEVEL_CODES[0];
};

/**
 * Checks if a brand is excluded from Earnings Summary display.
 * Uses AEM-authored excludedBrands array for configurability.
 * @param brandId - Brand code from the recent stay
 * @param excludedBrands - AEM-authored array of brand codes to exclude
 * @returns true if the brand should be excluded
 */
export const isExcludedFromEarningsSummary = (
  brandId: string | undefined,
  excludedBrands: string[] | undefined
): boolean => {
  if (!brandId || !excludedBrands || excludedBrands.length === 0) return false;
  return excludedBrands.some(code => code.toUpperCase() === brandId.toUpperCase());
};

/**
 * Checks if earnings summary should be displayed based on time window
 * Shows starting 72 hours after postDate (when earnings become available)
 * Hides 30 days after stayEndDate (stay checkout date)
 */
export const shouldShowEarningsSummary = (stayData: RecentStayData | null, currentDate: Date = new Date()): boolean => {
  if (!stayData) return false;

  const { stayEndDate, totalEliteNightCredits, totalPoints } = stayData;
  const hasEarnings = totalEliteNightCredits > 0 || totalPoints > 0;
  if (!hasEarnings) return false;

  const stayEndDateTime = new Date(stayEndDate).getTime();
  const currentDateTime = currentDate.getTime();

  // Start showing from the stay end date
  const hasPassedMinWaitPeriod = currentDateTime >= stayEndDateTime;

  // Stop showing: 30 days after stay end date
  const daysSinceStayEnd = (currentDateTime - stayEndDateTime) / (1000 * 60 * 60 * 24);
  const isWithinMaxWindow = daysSinceStayEnd <= EARNINGS_SUMMARY_CONSTANTS.MAX_DAYS_POST_STAY;

  return hasPassedMinWaitPeriod && isWithinMaxWindow;
};

/**
 * Gets the most recent qualifying stay from activity edges
 * Filters for stays with earnings and returns the most recent one
 */
export const getMostRecentQualifyingStay = (
  activityEdges: ActivityEdge[],
  halfEncBrands?: string[],
  zeroEncBrands?: string[]
): RecentStayData | null => {
  if (!activityEdges || activityEdges.length === 0) return null;

  const qualifyingStays = activityEdges
    .map(edge => edge.node)
    .filter(activity => {
      const isStay = activity.type?.code === constants.STAY;
      const isQualifying = activity.isQualifyingActivity === true;
      const hasEarnings =
        (activity.eliteEarning && activity.eliteEarning > 0) || (activity.totalEarning && activity.totalEarning > 0);

      return isStay && isQualifying && hasEarnings;
    });

  if (qualifyingStays.length === 0) return null;

  const sortedStays = qualifyingStays.sort((a, b) => {
    const dateA = new Date(a.postDate).getTime();
    const dateB = new Date(b.postDate).getTime();
    return dateB - dateA;
  });

  const recentStay = sortedStays[0];

  const stayData: RecentStayData = {
    propertyName: recentStay.properties?.[0]?.basicInformation?.name || '',
    propertyId: recentStay.properties?.[0]?.id || '',
    brandId: recentStay.properties?.[0]?.basicInformation?.brand?.id || '',
    stayStartDate: recentStay.startDate || '',
    stayEndDate: recentStay.endDate || '',
    postDate: recentStay.postDate || '',
    numberOfNights: calculateNights(recentStay.startDate, recentStay.endDate),
    totalEliteNightCredits: calculateENCsByBrand(
      calculateNights(recentStay.startDate, recentStay.endDate),
      recentStay.properties?.[0]?.basicInformation?.brand?.id,
      halfEncBrands,
      zeroEncBrands
    ),
    totalPoints: recentStay.totalEarning || 0,
    basePoints: recentStay.baseEarning || 0,
    elitePoints: recentStay.eliteEarning || 0,
    bonusPoints: recentStay.extraEarning || 0,
    currencyCode: recentStay.currency?.code, // 'HP' for Points, 'MI' for Miles - provided by activity table
    isQualifyingActivity: recentStay.isQualifyingActivity || false,
    activityType: recentStay.type?.code || '',
    activityDescription: recentStay.description || '',
  };

  return stayData;
};

/**
 * Formats stay date range (e.g., "Jan 15 - 17" or "Jan 30 - Feb 2")
 * @param locale - Locale from PageContext (required, no fallback)
 */
export const formatStayDateRange = (startDate: string, endDate: string, locale: string): string => {
  if (!startDate || !endDate || !locale) return '';

  const start = new Date(startDate);
  const end = new Date(endDate);

  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const dayFormatter = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' });

  const startMonth = monthFormatter.format(start);
  const endMonth = monthFormatter.format(end);
  const startDay = dayFormatter.format(start);
  const endDay = dayFormatter.format(end);

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

/**
 * Formats nights label (singular "night" vs plural "nights")
 * Converts label from AEM to singular form when nights = 1
 * @param nightsLabel - Plural nights label from AEM (required, no fallback)
 */
export const formatNightsLabel = (nights: number, nightsLabel: string): string => {
  if (!nightsLabel) return '';

  if (nights === 1) {
    const singularLabel = nightsLabel.replace(/nights/gi, match => {
      return match === 'nights' ? 'night' : 'Night';
    });
    return `${nights} ${singularLabel}`;
  }

  return `${nights} ${nightsLabel}`;
};

/**
 * Gets Elite Night Credits subtext for Near Achieving status
 * Returns message like "You are 3 nights away from Gold Elite"
 *
 * @param levelTypeCode - Current tier code
 * @param totalNights - Total nights earned this year
 * @param totalSpend - Total spend this year (for Titanium/Ambassador)
 * @param nightsLabel - Singular night label from AEM (e.g., "night")
 * @param nightsLabelPlural - Plural nights label from AEM (e.g., "nights")
 * @param messageTemplate - Message template from AEM (e.g., "You are {0} {1} away from {2}") where {0}=nights, {1}=night/nights, {2}=tierName
 * @param tierNames - Tier name overrides from AEM (optional)
 */
export const getEliteNightCreditsSubtext = (
  levelTypeCode: string | undefined,
  totalNights: number,
  totalSpend: number = 0,
  nightsLabel: string,
  nightsLabelPlural: string,
  messageTemplate?: string,
  tierNames?: Record<string, string>
): string | undefined => {
  // Return undefined if required AEM data is missing
  if (!messageTemplate || !nightsLabel || !nightsLabelPlural) {
    return undefined;
  }

  // Reuse existing getMemberStatus from shared helper
  const memberStatus = getMemberStatus(levelTypeCode, totalNights, totalSpend);

  if (memberStatus !== MEMBER_STATUS.NEAR_ACHIEVING) {
    return undefined;
  }

  const targetTier = getTargetTierForBenefits(levelTypeCode, memberStatus);

  // Use tier names from AEM if provided, fallback to constants only for tier name mapping
  const tierNameMap = tierNames || EARNINGS_SUMMARY_CONSTANTS.TIER_NAMES;
  const targetTierName = tierNameMap[targetTier];

  if (!targetTierName) {
    return undefined;
  }

  const nightsRequired = EARNINGS_SUMMARY_CONSTANTS.TIER_NIGHT_REQUIREMENTS[targetTier];
  if (!nightsRequired) {
    return undefined;
  }

  const nightsRemaining = nightsRequired - totalNights;

  if (nightsRemaining <= 0) {
    return undefined;
  }

  const nightLabel = nightsRemaining === 1 ? nightsLabel : nightsLabelPlural;

  return replaceText(messageTemplate, [nightsRemaining.toString(), nightLabel, targetTierName]);
};

/**
 * Processes hotel property data to extract the best available image URL
 * Priority: Primary Image → Gallery Image → Fallback Image
 *
 * @param hotelData - Property data from GraphQL (propertiesByIds)
 * @param locale - Current locale for image domain
 * @returns Image URL string or undefined if no hotel data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getHotelImageUrl = (hotelData: any, locale: string): string | undefined => {
  if (!hotelData) return undefined;

  const primaryImage = hotelData?.media?.primaryImage?.edges?.[0]?.node?.imageUrls?.wideHorizontal;
  const galleryImages = hotelData?.media?.photoGallery?.hotelView?.edges?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (img: any) => img?.node?.imageUrls?.wideHorizontal
  );

  // Priority: Primary Image → Gallery Image → Fallback
  const primaryImageURL = primaryImage ? getImageDomain(locale) + primaryImage : null;
  const galleryImageURL =
    galleryImages && galleryImages.length > 0
      ? getImageDomain(locale) + galleryImages[0]?.node?.imageUrls?.wideHorizontal
      : null;

  return primaryImageURL || galleryImageURL || getFallbackImage(locale).Classic;
};

/**
 * Check if a brand has a dedicated store
 * @param brandId - The brand ID to check
 * @returns true if brand has a store, false otherwise
 */
export const hasBrandStore = (brandId: string | undefined): boolean => {
  if (!brandId) return false;
  return EARNINGS_SUMMARY_CONSTANTS.BRANDS_WITH_STORES.includes(brandId);
};

/**
 * Determines the banner configuration based on A/B test variation and brand
 *
 * This function extracts tracking properties directly from shopCardDetails array:
 * - shopCardDetails[0]: Default "Shop Your Favorite Hotels" placement
 * - shopCardDetails[1]: "Book Your Next Stay" placement
 *
 * Each shopCardDetails object contains:
 * - trackingContentPosition: "Earnings Summary"
 * - trackingDescription: "Shop Placement"
 * - clickTrack: true/false
 * - impressionTrack: false
 * - impressionCount: false
 *
 * @param variation - Current A/B test variation
 * @param brandConfigs - Array of brand store configurations
 * @param brandId - Current brand ID from recent stay
 * @param model - AEM model with banner configurations
 * @returns Banner configuration object with tracking properties
 */
export const determineBannerConfig = (
  variation: 'shop' | 'book' | null,
  brandConfigs: BrandStoreConfig[],
  brandId: string | undefined,
  model: EarningsSummaryModel
): {
  featuredDynamicImage?: import('./EarningsSummary.types').DynamicMediaImage;
  headingText?: string;
  descriptionText?: string;
  ctaPath?: string;
  eyebrow?: string;
  trackingContentPosition?: string;
  trackingDescription?: string;
  clickTrack?: boolean;
} => {
  // A/B test not enabled - show generic external promo banner
  if (!variation) {
    const dynamicImage =
      model?.featuredDynamicExternalPromoBannerImage ||
      (model?.externalPromoBannerImage
        ? {
            altText: model?.externalPromoBannerImageAlt || '',
            assetPath: model?.externalPromoBannerImage,
            dynamic: false,
            renditions: [],
          }
        : undefined);

    return {
      featuredDynamicImage: dynamicImage,
      headingText: model?.externalPromoBannerTitle,
      descriptionText: model?.externalPromoBannerSubtitle,
      ctaPath: model?.externalPromoBannerCtaPath,
    };
  }

  // A/B Test enabled - Check variation FIRST (50% Book, 50% Shop)
  if (variation === EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.BOOK) {
    // Show "Book" placement - always shows book regardless of brand
    const bookCard = model?.shopCardDetails?.[1];
    const fallbackDynamicImage =
      model?.featuredDynamicExternalPromoBannerImage ||
      (model?.externalPromoBannerImage
        ? {
            altText: model?.externalPromoBannerImageAlt || '',
            assetPath: model?.externalPromoBannerImage,
            dynamic: false,
            renditions: [],
          }
        : undefined);

    return {
      featuredDynamicImage: bookCard?.featuredDynamicImageUrl || fallbackDynamicImage,
      headingText: bookCard?.shopCardHeader || model?.externalPromoBannerTitle,
      descriptionText: bookCard?.shopCardDescription || model?.externalPromoBannerSubtitle,
      ctaPath: bookCard?.shopCardCtaPath || model?.externalPromoBannerCtaPath,
      trackingContentPosition: bookCard?.trackingContentPosition,
      trackingDescription: bookCard?.trackingDescription,
      clickTrack: bookCard?.clickTrack,
    };
  }

  // Variation is "Shop" - Check if brand has a store
  const brandConfig = brandConfigs.find(config => config.brandID === brandId);

  // If brand has a store, show brand-specific shop banner
  if (brandConfig) {
    return {
      featuredDynamicImage: brandConfig.featuredDynamicBookImageUrl,
      headingText: brandConfig.headerCopy,
      descriptionText: brandConfig.descriptionCopy,
      ctaPath: brandConfig.ctaPath,
      eyebrow: brandConfig.eyebrow,
      trackingContentPosition: brandConfig.trackingContentPosition,
      trackingDescription: brandConfig.trackingDescription,
      clickTrack: brandConfig.clickTrack,
    };
  }

  // Shop variation but no brand store - show default "Shop Your Favorite Hotels"
  const defaultShopCard = model?.shopCardDetails?.[0];
  return {
    featuredDynamicImage: defaultShopCard?.featuredDynamicImageUrl,
    headingText: defaultShopCard?.shopCardHeader,
    descriptionText: defaultShopCard?.shopCardDescription,
    ctaPath: defaultShopCard?.shopCardCtaPath,
    trackingContentPosition: defaultShopCard?.trackingContentPosition,
    trackingDescription: defaultShopCard?.trackingDescription,
    clickTrack: defaultShopCard?.clickTrack,
  };
};

/**
 * Get or set the A/B test variation for the current page load with alternating pattern
 * Uses localStorage to track last shown variation and alternates on each page refresh
 * @param enableTest - Whether A/B test is enabled
 * @returns The placement variation for this page load (alternates on each refresh)
 */
export const getSessionVariation = (enableTest: boolean): 'shop' | 'book' | null => {
  if (!enableTest) {
    return null;
  }

  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    // Server-side rendering
    return EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP; // Default to shop
  }

  try {
    const STORAGE_KEY = EARNINGS_SUMMARY_CONSTANTS.VARIATION_SESSION_KEY;

    // Get the last shown variation from localStorage
    const lastVariation = localStorage.getItem(STORAGE_KEY);

    let newVariation: 'shop' | 'book';

    if (
      !lastVariation ||
      (lastVariation !== EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP &&
        lastVariation !== EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.BOOK)
    ) {
      // First time or invalid value - start with random
      newVariation =
        Math.random() < 0.5
          ? EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP
          : EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.BOOK;
    } else {
      // Alternate from last shown
      newVariation =
        lastVariation === EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP
          ? EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.BOOK
          : EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP;
    }

    // Store the new variation for next page load
    localStorage.setItem(STORAGE_KEY, newVariation);

    return newVariation;
  } catch (error) {
    // Default to shop variation on error (localStorage might be blocked)
    return EARNINGS_SUMMARY_CONSTANTS.PLACEMENT_VARIATIONS.SHOP;
  }
};

/**
 * Gets the appropriate icon color for earnings display
 * For Member tier (R), returns memberSolid/accent10 instead of base10 (black)
 * For all other tiers, uses getColorByCode
 *
 * @param levelTypeCode - The member's level type code
 * @param getColorByCode - The global color function
 * @param baseVariables - Base variables containing colors
 * @param MEMBERSHIP_CODES - Membership code constants
 * @returns Color string for icon display
 */
export const getEarningsIconColor = (
  levelTypeCode: string | undefined,
  getColorByCode: (code: string, isBackground: boolean) => string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  baseVariables: any,
  MEMBERSHIP_CODES: { MEMBER_CODES: string[] }
): string => {
  const color = getColorByCode(levelTypeCode || '', false);
  // For Member tier, use memberSolid/accent10 instead of base10 (black)
  if (!levelTypeCode || MEMBERSHIP_CODES.MEMBER_CODES.includes(levelTypeCode)) {
    return baseVariables.color.memberSolid || baseVariables.color.accent10 || baseVariables.color.base10;
  }
  return color;
};

/**
 * Gets the appropriate light background color for tier-specific promotional banners
 * Reuses getMemberLevelPropertiesByCode from global helper to avoid color duplication
 *
 * @param levelTypeCode - The member's level type code
 * @param memberStatusList - Member status list for tier mapping
 * @param baseVariables - Base variables for fallback colors
 * @returns Background color string for tier (from memerLevelBackground property)
 */
export const getTierLightBackground = (
  levelTypeCode: string | undefined,
  memberStatusList: Array<{ id: string; label: string }> | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  baseVariables: any
): string => {
  // Default to member background variable
  const defaultBackground = baseVariables?.color?.memberSolidAlt || baseVariables?.color?.neutral10;

  if (!levelTypeCode || !memberStatusList) {
    return defaultBackground;
  }

  try {
    const properties = getMemberLevelPropertiesByCode(levelTypeCode, memberStatusList);
    return properties.memerLevelBackground || defaultBackground;
  } catch (error) {
    return defaultBackground; // Fallback on error
  }
};

/**
 * Checks if user has any registered promotions with earnings
 * Used to display the "Extra earnings from promotions" banner in Earnings Summary (WEB-164907)
 *
 * @param promotions - Promotions data from Zustand store (fetched by RegisteredPromotions/NewPromotions)
 * @returns true if user has at least one registered promotion with earnings, false otherwise
 */
export const hasRegisteredPromotionEarnings = (promotions: RootPromotions | null): boolean => {
  // Guard: No promotions data
  if (!promotions?.loyaltyPromotions?.edges?.length) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for accurate date comparison

  // Filter for registered promotions that:
  // 1. Have a state code in REGISTERED_PROMOTIONS_STATE_CODES
  // 2. Have not expired (endDate is today or in the future)
  // 3. Have earnings with quantity > 0
  const registeredPromotionsWithEarnings = promotions.loyaltyPromotions.edges.filter(promo => {
    // Guard: No node data
    if (!promo?.node) {
      return false;
    }

    const stateCode = promo.node.state?.code;

    // Guard: No state code or not a registered state
    if (!stateCode) {
      return false;
    }
    const isRegistered = PROMOTIONS_CONSTANT.REGISTERED_PROMOTIONS_STATE_CODES.includes(stateCode);
    if (!isRegistered) return false;

    // Check if promotion has not expired
    const endDate = promo.node.offers?.[0]?.hurdle?.endDate;
    if (endDate) {
      const promotionEndDate = new Date(endDate);
      promotionEndDate.setHours(0, 0, 0, 0);
      // If promotion ended before today, exclude it
      if (promotionEndDate < today) return false;
    }

    // Check if promotion has earnings with quantity > 0
    const earnings = promo.node.earnings;

    // Guard: No earnings array
    if (!earnings || !Array.isArray(earnings) || earnings.length === 0) {
      return false;
    }

    // Must have at least one earning with quantity > 0
    const hasEarnings = earnings.some(earning => {
      return earning && typeof earning.quantity === 'number' && earning.quantity > 0;
    });

    return hasEarnings;
  });

  return registeredPromotionsWithEarnings.length > 0;
};
